import React, { useState } from 'react';
import { IonActionSheet } from '@ionic/react';
import { ContactLogic } from './logic';
import type { Contact } from './types';
import styles from './RecipientChip.module.css';

/**
 * One recipient's own detail — their name, where a message addresses them —
 * rendered as a chip inline with the text around it. Tapping it offers every
 * recipient in a picker, so the sender can see the same text for someone
 * else. It only says who was picked; whoever renders the text decides what
 * that changes.
 */
const RecipientChip: React.FC<{
  /** The detail as it reads for the recipient shown now. */
  text: string;
  /** Everyone the picker offers. */
  recipients: readonly Contact[];
  onPick: (recipient: Contact) => void;
  /** What the picker asks, e.g. "Preview the message to". */
  pickerHeader: string;
  /** Says what tapping does, for someone not seeing the chip. */
  ariaLabel: string;
  disabled?: boolean;
  testId: string;
}> = ({
  text,
  recipients,
  onPick,
  pickerHeader,
  ariaLabel,
  disabled,
  testId,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={styles.chip}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setPickerOpen(true)}
        data-testid={testId}
      >
        {text}
      </button>
      <IonActionSheet
        isOpen={pickerOpen}
        onDidDismiss={() => setPickerOpen(false)}
        header={pickerHeader}
        data-testid={`${testId}-picker`}
        buttons={[
          ...recipients.map(recipient => ({
            text: ContactLogic.displayName(recipient),
            handler: () => onPick(recipient),
          })),
          { text: 'Cancel', role: 'cancel' as const },
        ]}
      />
    </>
  );
};

export default RecipientChip;
